import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Calendar, TrendingUp, DollarSign } from 'lucide-react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import AIQueryBox from '../components/reports/AIQueryBox';
import { toast } from 'sonner';

const QuotationReport = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [reportData, setReportData] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);

  useEffect(() => {
    // Set default date range (last 30 days)
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  }, []);

  const handleQuery = async (query) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/reports/ai-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          reportType: 'quotation',
          dateRange
        })
      });

      if (!response.ok) throw new Error('Query failed');

      const data = await response.json();
      setAiResponse(data);
      toast.success('Query processed successfully!');
    } catch (error) {
      toast.error('Failed to process query');
      console.error(error);
    }
  };

  const metrics = [
    { label: 'Total Rate Quotes', value: '721', change: '+18%', icon: FileText, color: 'from-blue-500 to-blue-600' },
    { label: 'Conversion Rate', value: '29%', change: '+5%', icon: TrendingUp, color: 'from-green-500 to-green-600' },
    { label: 'Total Profit', value: '$499,637', change: '+12%', icon: DollarSign, color: 'from-purple-500 to-purple-600' },
    { label: 'Booked Quotes', value: '209', change: '+22%', icon: FileText, color: 'from-orange-500 to-orange-600' },
  ];

  const lineChartData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Quotes Created',
      data: [165, 192, 178, 186],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const barChartData = {
    labels: ['Lawn & Garden', 'Auto & HHG', 'Machinery', 'Electrical Parts', 'General Cargo'],
    datasets: [{
      label: 'Quotes by Category',
      data: [165, 89, 75, 58, 45],
      backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6']
    }]
  };

  const doughnutChartData = {
    labels: ['Booked', 'Pending', 'Expired', 'Rejected'],
    datasets: [{
      data: [209, 342, 128, 42],
      backgroundColor: ['#10B981', '#F59E0B', '#6B7280', '#EF4444']
    }]
  };

  // Customer Data
  const customerData = [
    { customer: 'STIGA SPA-CASTELFRANCO VENETO', totalQuote: 152, booked: 38, bookingPercent: 25.00, avgProfit: 35.84 },
    { customer: 'TRANSPORTMANAGEMENT SRL-PESCHIERA BORROMEO', totalQuote: 48, booked: 14, bookingPercent: 29.17, avgProfit: 32.56 },
    { customer: 'MESCQ EGYPT (MARINE & ENGINEERING)', totalQuote: 42, booked: 9, bookingPercent: 21.43, avgProfit: 28.45 },
    { customer: 'TRANS WORLD SHIPPING-TOLEDO', totalQuote: 38, booked: 12, bookingPercent: 31.58, avgProfit: 30.12 },
    { customer: 'STIGA SLOVAKIA S.R.O.-POPRA', totalQuote: 32, booked: 11, bookingPercent: 34.38, avgProfit: 33.78 },
    { customer: 'R & M INTERNATIONAL-HORRISTOWN', totalQuote: 28, booked: 8, bookingPercent: 28.57, avgProfit: 29.91 },
    { customer: 'FAST FORWARD LOGISTICS CO.-EGYPT', totalQuote: 24, booked: 6, bookingPercent: 25.00, avgProfit: 27.34 },
    { customer: 'JSB EQUIPMENTS INC-HICKSVILLE', totalQuote: 18, booked: 5, bookingPercent: 27.78, avgProfit: 31.22 },
  ];

  const customerChartData = {
    labels: customerData.map(d => d.customer.split('-')[0]),
    datasets: [{
      label: 'Booking %',
      data: customerData.map(d => d.bookingPercent),
      backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6']
    }]
  };

  // Sales Person Data
  const salesPersonData = [
    { salesPerson: 'MANAGEMENT', totalQuote: 248, booked: 72, bookingPercent: 29.03, avgProfit: 34.21 },
    { salesPerson: 'Simone Penzo', totalQuote: 156, booked: 45, bookingPercent: 28.85, avgProfit: 31.45 },
    { salesPerson: 'NJ SALES', totalQuote: 142, booked: 38, bookingPercent: 26.76, avgProfit: 29.87 },
    { salesPerson: 'Liszette Avellano', totalQuote: 78, booked: 24, bookingPercent: 30.77, avgProfit: 32.96 },
    { salesPerson: 'Carlos Ruiz', totalQuote: 45, booked: 14, bookingPercent: 31.11, avgProfit: 28.54 },
    { salesPerson: 'Pam Donovan', totalQuote: 32, booked: 10, bookingPercent: 31.25, avgProfit: 30.12 },
    { salesPerson: 'Andrea Arditi', totalQuote: 20, booked: 6, bookingPercent: 30.00, avgProfit: 27.89 },
  ];

  const salesPersonChartData = {
    labels: salesPersonData.map(d => d.salesPerson),
    datasets: [{
      label: 'Total Quotes',
      data: salesPersonData.map(d => d.totalQuote),
      backgroundColor: '#3B82F6'
    }, {
      label: 'Booked',
      data: salesPersonData.map(d => d.booked),
      backgroundColor: '#10B981'
    }]
  };

  // Customer Not Booked Data
  const customerNotBookedData = [
    { customer: 'KUBOTA GIANNI FERRARI SRL-REGGIOLO', totalQuote: 24 },
    { customer: 'GAMECHANGE SOLAR-NORWALK', totalQuote: 18 },
    { customer: 'CYCLONE SHIPPING-GILBERT', totalQuote: 16 },
    { customer: 'EMMANUEL KWAKU NYAME-BLACKLICK', totalQuote: 14 },
    { customer: 'SUPER TRADING LLC-MINNEAPOLIS', totalQuote: 12 },
    { customer: 'BMF ROOFING SYSTEMS SDN BHD', totalQuote: 11 },
    { customer: 'GAP-PARSUPPANNY', totalQuote: 10 },
    { customer: 'ATLANTIC PACIFIC EXPRESS SHIPPING-NILES', totalQuote: 9 },
    { customer: 'TRANS OVERSEAS-ROMULUS', totalQuote: 8 },
    { customer: 'ISS GLOBAL FORWARDING SOUTH AFRICA', totalQuote: 7 },
  ];

  const customerNotBookedChartData = {
    labels: customerNotBookedData.map(d => d.customer.split('-')[0]),
    datasets: [{
      label: 'Total Quotes (Not Booked)',
      data: customerNotBookedData.map(d => d.totalQuote),
      backgroundColor: ['#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E', '#10B981', '#14B8A6']
    }]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-8 h-8" />
            <div>
              <h1 className="text-3xl font-bold">Quotation Report</h1>
              <p className="text-white/90 text-lg">AI-powered quotation analytics and insights</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5" />
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <span className="text-white/70">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={index}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-gradient-to-br ${metric.color} rounded-xl shadow-lg p-6 text-white`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon className="w-5 h-5" />
                <h3 className="text-sm font-medium opacity-80">{metric.label}</h3>
              </div>
              <p className="text-3xl font-bold">{metric.value}</p>
              <p className="text-sm opacity-80 mt-1">{metric.change} from last period</p>
            </motion.div>
          );
        })}
      </div>

      {/* AI Query Box */}
      <AIQueryBox onQuery={handleQuery} reportType="quotation" />

      {/* AI Response */}
      {aiResponse && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl shadow-sm p-6 border border-purple-200 dark:border-purple-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3 flex items-center">
            <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            AI Insights
          </h3>
          <div className="prose dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{aiResponse.response}</p>
          </div>
        </motion.div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Quote Trend</h3>
          <div style={{ height: '300px' }}>
            <Line data={lineChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Quote Status</h3>
          <div style={{ height: '300px' }}>
            <Doughnut data={doughnutChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </motion.div>
      </div>

      {/* Report Summary By Customer */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4">
          <h3 className="text-xl font-bold text-white">Report Summary By Customer</h3>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Customer</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Total Quote</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Booked</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Booking %</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Avg. Profit Margin</th>
                </tr>
              </thead>
              <tbody>
                {customerData.map((row, index) => (
                  <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="py-3 px-4 text-sm text-slate-900 dark:text-white font-medium">{row.customer}</td>
                    <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{row.totalQuote}</td>
                    <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{row.booked}</td>
                    <td className="py-3 px-4 text-sm text-center font-semibold text-blue-600 dark:text-blue-400">{row.bookingPercent.toFixed(2)}%</td>
                    <td className="py-3 px-4 text-sm text-center font-semibold text-blue-600 dark:text-blue-400">{row.avgProfit.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Report Summary By Sales Person */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4">
          <h3 className="text-xl font-bold text-white">Report Summary By Sales Person</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="overflow-x-auto border-r border-slate-200 dark:border-slate-700 pr-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Sales Person</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Total Quote</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Booked</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Booking %</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Avg. Profit Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {salesPersonData.map((row, index) => (
                    <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="py-3 px-4 text-sm text-slate-900 dark:text-white font-medium">{row.salesPerson}</td>
                      <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{row.totalQuote}</td>
                      <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{row.booked}</td>
                      <td className="py-3 px-4 text-sm text-center font-semibold text-blue-600 dark:text-blue-400">{row.bookingPercent.toFixed(2)}%</td>
                      <td className="py-3 px-4 text-sm text-center font-semibold text-blue-600 dark:text-blue-400">{row.avgProfit.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pl-6">
              <h4 className="text-md font-bold text-slate-900 dark:text-white mb-4">Sales Person Performance Comparison</h4>
              <div style={{ height: '300px' }}>
                <Bar data={salesPersonChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Report Summary By Customer Not Booked */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-red-600 to-orange-600 p-4">
          <h3 className="text-xl font-bold text-white">Report Summary By Customer Not Booked</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="overflow-x-auto border-r border-slate-200 dark:border-slate-700 pr-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Customer</th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Total Quote</th>
                  </tr>
                </thead>
                <tbody>
                  {customerNotBookedData.map((row, index) => (
                    <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="py-3 px-4 text-sm text-slate-900 dark:text-white font-medium">{row.customer}</td>
                      <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{row.totalQuote}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pl-6">
              <h4 className="text-md font-bold text-slate-900 dark:text-white mb-4">Top Customers with Unbooked Quotes</h4>
              <div style={{ height: '300px' }}>
                <Bar data={customerNotBookedChartData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y' }} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QuotationReport;